import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { hapticImpact } from "../lib/telegram";

const MAX_LEN = 500;

export interface ChatMessage {
  id: number;
  senderId: string;
  senderName: string;
  body: string;
  createdAt: string;
}

// Presentational chat surface shared by the alliance and server channels.
// Own messages align right; others show the sender name. New messages stick
// to the bottom; scrolling to the top loads older history while keeping the
// viewport anchored.
export function ChatPanel({
  messages,
  myId,
  onSend,
  pending,
  isLoading,
  emptyText = "Пока тихо. Напиши первое сообщение.",
  header,
  onLoadOlder,
  loadingOlder = false,
  hasMore = false,
}: {
  messages: ChatMessage[];
  myId: string;
  onSend: (body: string) => void;
  pending: boolean;
  isLoading: boolean;
  emptyText?: string;
  header?: ReactNode;
  onLoadOlder?: () => void;
  loadingOlder?: boolean;
  hasMore?: boolean;
}) {
  const [text, setText] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Previous frame's first/last id + scrollHeight, to tell a top-prepend
  // (preserve position) from a bottom-append (follow to the latest).
  const prev = useRef({ firstId: 0, lastId: 0, scrollHeight: 0 });

  const firstId = messages.length ? messages[0].id : 0;
  const lastId = messages.length ? messages[messages.length - 1].id : 0;

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const p = prev.current;
    const prepended = p.firstId !== 0 && firstId < p.firstId && lastId === p.lastId;
    if (prepended) {
      // Keep the same message under the user's eyes as content grows upward.
      el.scrollTop = el.scrollTop + (el.scrollHeight - p.scrollHeight);
    } else if (lastId !== p.lastId) {
      // New messages (or first load): follow to the bottom only if the user is
      // already near it, so reading history isn't interrupted.
      const nearBottom =
        p.scrollHeight === 0 || el.scrollHeight - el.scrollTop - el.clientHeight < 220;
      if (nearBottom) el.scrollTop = el.scrollHeight;
    }
    prev.current = { firstId, lastId, scrollHeight: el.scrollHeight };
  }, [firstId, lastId, messages.length]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el || !onLoadOlder || !hasMore || loadingOlder) return;
    if (el.scrollTop < 48) onLoadOlder();
  };

  const send = () => {
    const body = text.trim();
    if (!body || pending) return;
    hapticImpact("light");
    onSend(body);
    setText("");
  };

  return (
    <div className="flex h-full flex-col">
      {header}
      <div ref={scrollRef} onScroll={onScroll} className="flex-1 space-y-2 overflow-y-auto px-3 pb-2">
        {loadingOlder && (
          <div className="py-2 text-center text-xs text-tg-hint">Загружаю историю…</div>
        )}
        {isLoading ? (
          <div className="py-8 text-center text-sm text-tg-hint">Загружаю чат…</div>
        ) : messages.length === 0 ? (
          <div className="py-8 text-center text-sm text-tg-hint">{emptyText}</div>
        ) : (
          messages.map((m) => {
            const mine = m.senderId === myId;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={
                    "max-w-[80%] rounded-2xl px-3 py-2 " +
                    (mine ? "bg-tg-button text-tg-buttonText" : "bg-tg-secondaryBg text-tg-text")
                  }
                >
                  {!mine && (
                    <div className="mb-0.5 text-[11px] font-medium text-tg-accent">
                      {m.senderName}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words text-sm">{m.body}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-end gap-2 px-3 pb-3 pt-1">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder="Сообщение…"
          className="max-h-28 flex-1 resize-none rounded-2xl bg-tg-secondaryBg px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={send}
          disabled={!text.trim() || pending}
          className="rounded-2xl bg-tg-button px-4 py-2 text-sm font-medium text-tg-buttonText disabled:opacity-40"
        >
          {pending ? "…" : "→"}
        </button>
      </div>
    </div>
  );
}
